package com.tfc.care.tfccare.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.AttachFile
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.CopyAll
import androidx.compose.material.icons.outlined.DoneAll
import androidx.compose.material.icons.outlined.Download
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tfc.care.tfccare.copyToClipboard
import com.tfc.care.tfccare.domain.model.*
import com.tfc.care.tfccare.downloadImageFromUrl
import com.tfc.care.tfccare.ui.components.TfcPayLogoDark
import com.tfc.care.tfccare.ui.components.TfcPayLogoLight
import com.tfc.care.tfccare.utils.formatTime
import com.tfc.care.tfccare.utils.isDarkTheme
import com.tfc.care.tfccare.utils.toConversationTime
import com.tfc.care.tfccare.utils.toDateHeader
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StaffChatScreen(
    viewModel: StaffChatViewModel,
    onLogOut: () -> Unit,
    onImagePick: () -> Unit,
    imageLoader: @Composable (String) -> Unit,
    onImageClick: (String) -> Unit,
    showMessage: (String) -> Unit,
) {
    val chatState by viewModel.chatState.collectAsState()
    val staff by viewModel.staff.collectAsState()
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isMobile = maxWidth < 600.dp

        Row(
            modifier = Modifier
                .fillMaxSize()
                .focusRequester(focusRequester)
                .focusable()
                .onKeyEvent {
                    if (it.type == KeyEventType.KeyDown && it.key == Key.Escape) {
                        viewModel.deselectConversation()
                        true
                    } else {
                        false
                    }
                }) {

            if (!isMobile || chatState.selectedUser == null) {
                ConversationsList(
                    modifier = if (isMobile) Modifier.fillMaxSize() else Modifier.width(350.dp),
                    conversations = chatState.conversations,
                    selectedUserId = chatState.selectedUser?.userId,
                    onLogout = {
                        onLogOut()
                    },
                    onConversationClick = { conversation ->
                        viewModel.selectConversation(conversation)
                    },
                    staff = staff
                )
            }

            if (!isMobile || chatState.selectedUser != null) {
                Column(Modifier.fillMaxSize()) {
                    // Theme toggle above ChatArea
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (isMobile && chatState.selectedUser != null) {
                            IconButton(onClick = { viewModel.deselectConversation() }) {
                                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                            }
                        }

                        chatState.selectedUser?.let { s ->
                            Text(
                                text = "${s.userName} ( ${s.userNumber} )",
                                modifier = Modifier.weight(1f, fill = false),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Icon(
                                imageVector = Icons.Outlined.CopyAll,
                                contentDescription = "Copy all",
                                modifier = Modifier.padding(start = 8.dp).clickable {
                                    copyToClipboard(s.userNumber)
                                }
                            )
                        }

                        Spacer(modifier = Modifier.weight(1f))
                        if (!isMobile) {
                            Text(
                                text = if (chatState.isDarkTheme) "Dark Mode" else "Light Mode",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                        }
                        Switch(
                            checked = chatState.isDarkTheme,
                            onCheckedChange = { viewModel.toggleTheme() },
                            colors = SwitchDefaults.colors(
                                checkedThumbColor = MaterialTheme.colorScheme.primary,
                                uncheckedThumbColor = MaterialTheme.colorScheme.primaryContainer,
                                checkedTrackColor = MaterialTheme.colorScheme.primaryContainer,
                                uncheckedTrackColor = MaterialTheme.colorScheme.primary
                            )
                        )
                    }
                    if (chatState.selectedUser != null) {
                        ChatArea(
                            messages = chatState.messages,
                            isLoading = chatState.isLoading,
                            error = chatState.error,
                            onSendMessage = { text -> viewModel.sendMessage(text) },
                            onImagePick = onImagePick,
                            imageLoader = imageLoader,
                            showMessage = showMessage,
                            onImageClick = onImageClick
                        )
                    } else {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = "Select a conversation to start chatting",
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontSize = 18.sp
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun EngagementStatItem(label: String, count: Int) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(
            text = count.toString(),
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold
        )
        Text(
            text = label,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
            fontSize = 10.sp
        )
    }
}

@Composable
fun ConversationsList(
    modifier: Modifier = Modifier,
    conversations: List<Conversation>,
    selectedUserId: String?,
    onLogout: () -> Unit,
    onConversationClick: (Conversation) -> Unit,
    staff: Staff? = null,
) {
    var query by remember { mutableStateOf("") }
    Surface(
        modifier = modifier
            .fillMaxHeight(),
        color = MaterialTheme.colorScheme.surfaceVariant
    ) {
        Column {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.surfaceVariant
            ) {
                Column {
                    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        Image(
                            imageVector = if(isDarkTheme(MaterialTheme.colorScheme.background)) Icons.TfcPayLogoDark else Icons.TfcPayLogoLight,
                            contentDescription = "Logo",
                            modifier = Modifier.height(35.dp).padding(start = 16.dp),
                        )
                        Spacer(modifier = Modifier.weight(1f))
                        Text(
                            text = "Log Out",
                            modifier = Modifier.padding(16.dp).clickable {
                                onLogout()
                            },
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }

                    staff?.engagement?.let { engagement ->
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp)
                                .padding(bottom = 8.dp)
                        ) {
                            Text(
                                text = "Engagement Status",
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Bold
                            )
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                EngagementStatItem("Today", engagement.dayCount)
                                EngagementStatItem("Month", engagement.monthCount)
                                EngagementStatItem("Prev", engagement.previousMonthCount)
                            }
                        }
                    }
                }
            }

            OutlinedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = query,
                onValueChange = {
                    query = it
                },
                placeholder = { Text("Search") },
                prefix = { Icon(Icons.Default.Search, contentDescription = null) },
                suffix = {
                    if (query.isNotEmpty()) {
                        Icon(
                            Icons.Default.Clear,
                            contentDescription = null,
                            modifier = Modifier.clickable { query = "" })
                    }
                }
            )


            // Conversations list
            val filteredConversations = conversations.filter {
                it.userName.contains(query, ignoreCase = true) || 
                it.userNumber.contains(query, ignoreCase = true) ||
                (it.userName == "Loading..." && it.userId.contains(query, ignoreCase = true))
            }
            if (filteredConversations.isEmpty()) {
                Text(
                    text = "No conversations found",
                    modifier = Modifier.padding(16.dp),
                    color = Color.Gray,
                    fontSize = 16.sp
                )
            }
            LazyColumn {
                items(filteredConversations) { conversation: Conversation ->
                    ConversationItem(
                        conversation = conversation,
                        isSelected = conversation.userId == selectedUserId,
                        onClick = { onConversationClick(conversation) }
                    )
                }
            }
        }
    }
}

@Composable
fun ConversationItem(
    conversation: Conversation,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        color = if (isSelected) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
        tonalElevation = if (isSelected) 4.dp else 0.dp
    ) {
        Row(
            modifier = Modifier
                .padding(16.dp)
                .fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(MaterialTheme.colorScheme.primary),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = conversation.userName.firstOrNull()?.uppercase() ?: "?",
                    color = Color.White,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = conversation.userNumber.trim().ifEmpty { conversation.userName },
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        modifier = Modifier.weight(1f)
                    )

                    Text(
                        text = conversation.lastMessageTime.toConversationTime(),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(end = 8.dp)
                    )

                    if (conversation.unreadCount > 0) {
                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clip(CircleShape)
                                .background(MaterialTheme.colorScheme.error),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = conversation.unreadCount.toString(),
                                color = Color.White,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))

                Text(
                    text = conversation.lastMessage,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontSize = 14.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = conversation.companyName,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontSize = 10.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
fun ChatArea(
    messages: List<Message>,
    isLoading: Boolean,
    error: String?,
    onSendMessage: (String) -> Unit,
    onImagePick: () -> Unit,
    imageLoader: @Composable (String) -> Unit,
    onImageClick: (String) -> Unit,
    showMessage: (String) -> Unit
) {
    var messageText by remember { mutableStateOf("") }
    val listState = rememberLazyListState()

    LaunchedEffect(messages.size) {
        if (messages.isNotEmpty()) {
            listState.animateScrollToItem(messages.size + messages.groupBy { it.timestamp.toDateHeader() }.size - 1)
        }
    }

    Column(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            state = listState,
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            val groupedMessages = messages.groupBy { it.timestamp.toDateHeader() }

            groupedMessages.forEach { (date, messageList) ->
                item(key = date) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Surface(
                            color = MaterialTheme.colorScheme.secondaryContainer,
                            shape = RoundedCornerShape(16.dp)
                        ) {
                            Text(
                                text = date,
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSecondaryContainer
                            )
                        }
                    }
                }

                items(messageList, key = { it.id }) { message ->
                    StaffMessageItem(
                        showMessage = showMessage,
                        message = message,
                        imageLoader = imageLoader
                    ) { url ->
                        onImageClick(url)
                    }
                }
            }
        }

        // Error message
        error?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
            )
        }

        // Loading indicator
        if (isLoading) {
            LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
        }

        // Input area
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shadowElevation = 8.dp
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onImagePick) {
                    Icon(
                        imageVector = Icons.Default.AttachFile,
                        contentDescription = "Attach image"
                    )
                }

                OutlinedTextField(
                    value = messageText,
                    onValueChange = { messageText = it },
                    maxLines = 10,
                    modifier = Modifier
                        .weight(1f)
                        .onPreviewKeyEvent { event ->
                            if (
                                event.type == KeyEventType.KeyDown &&
                                event.key == Key.Enter
                            ) {
                                if (event.isShiftPressed) {
                                    // Shift+Enter: allow newline, don't consume
                                    false
                                } else {
                                    // Just Enter: send message
                                    if (messageText.isNotBlank()) {
                                        onSendMessage(messageText.trim())
                                        messageText = ""
                                    }
                                    true  // consume the event
                                }
                            } else {
                                false
                            }
                        },
                    placeholder = { Text("Type a message...") },
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Send),
                    keyboardActions = KeyboardActions(onSend = { onSendMessage(messageText.trim()) })
                )

                Spacer(modifier = Modifier.width(8.dp))

                IconButton(
                    onClick = {
                        if (messageText.isNotBlank()) {
                            onSendMessage(messageText.trim())
                            messageText = ""
                        }
                    },
                    enabled = messageText.isNotBlank()
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.Send,
                        contentDescription = "Send",
                        tint = if (messageText.isNotBlank())
                            MaterialTheme.colorScheme.primary
                        else
                            MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                    )
                }
            }
        }
    }
}

@Composable
fun StaffMessageItem(
    message: Message,
    imageLoader: @Composable (String) -> Unit,
    showMessage: (String) -> Unit,
    onImageClick: (String) -> Unit
) {
    val isStaffMessage = message.senderType == SenderType.STAFF
    val time = formatTime(message.timestamp)

    val scope = rememberCoroutineScope()
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (isStaffMessage) Arrangement.End else Arrangement.Start
    ) {
        Card(
            modifier = Modifier.widthIn(max = 400.dp),
            shape = RoundedCornerShape(12.dp),
            elevation = CardDefaults.elevatedCardElevation(4.dp),
            colors = CardDefaults.cardColors(
                containerColor = if (isStaffMessage)
                    MaterialTheme.colorScheme.primaryContainer
                else
                    MaterialTheme.colorScheme.surfaceVariant
            )
        ) {
            Column(modifier = Modifier.padding(12.dp)) {
                if (!isStaffMessage) {
                    Text(
                        text = "User",
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onBackground
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                }

                if (message.messageType == MessageType.IMAGE && message.imageUrl != null) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(200.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .clickable {
                                onImageClick(message.imageUrl)
                            }
                    ) {
                        imageLoader(message.imageUrl)
                    }
                    if (message.text.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(4.dp))
                    }
                }

                if (message.text.isNotEmpty()) {
                    Text(
                        text = message.text,
                        color = MaterialTheme.colorScheme.onBackground,
                        fontSize = 15.sp
                    )
                }

                Spacer(modifier = Modifier.height(4.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    if (message.messageType == MessageType.TEXT) {
                        Icon(
                            imageVector = Icons.Outlined.CopyAll,
                            contentDescription = "copy",
                            modifier = Modifier.padding(end = 4.dp).size(20.dp).clickable {
                                copyToClipboard(message.text)
                                showMessage("Copied to clipboard")
                            }
                        )
                    }


                    if (message.messageType == MessageType.IMAGE) {
                        Icon(
                            imageVector = Icons.Outlined.Download,
                            contentDescription = "copy",
                            modifier = Modifier.padding(end = 4.dp).size(20.dp).clickable {
                                val fileName = "${message.id}.jpg"
                                scope.launch {
                                    downloadImageFromUrl(
                                        imageUrl = message.imageUrl!!,
                                        fileName = fileName
                                    )
                                }
                                showMessage("Downloaded to Downloads folder@$fileName")
                            }
                        )
                    }

                    Spacer(modifier = Modifier.weight(1f))
                    Text(
                        text = time,
                        fontSize = 11.sp,
                        color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.7f)
                    )

                    if (isStaffMessage) {
                        Icon(
                            imageVector = Icons.Outlined.DoneAll,
                            contentDescription = null,
                            tint = if (message.read) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant.copy(
                                alpha = 0.6f
                            ),
                            modifier = Modifier.padding(end = 4.dp).size(20.dp)
                        )
                    }
                }
            }
        }
    }
}
